import { Trans } from "@lingui/macro";
import { IApiEpUserAuthorized } from "@smp/api";
import { UiBox, UiSpinner, UiTypography } from "@smp/ui";
import { testAppendAttr } from "@smp/utils";
import { FC, useLayoutEffect, useMemo } from "react";

import { useEpBillingPaymentProvider } from "@/hooks/use_ep_billing_payment_provider";
import { PaymentProviderCard } from "@/screens/app/components/payment_provider_card";
import { useContextApp } from "@/screens/app/context/app";

import { IInvoiceFormValues } from "../../use_invoice_form";

export interface IPaymentProviderSelectorProps {
  paymentType: IInvoiceFormValues["paymentType"];
  product: IInvoiceFormValues["product"];
  value: IInvoiceFormValues["paymentProvider"];
  onChange: (value: IPaymentProviderSelectorProps["value"]) => void;
}

const isValidProvider = (
  provider: Exclude<IInvoiceFormValues["paymentProvider"], undefined>,
  paymentType: IInvoiceFormValues["paymentType"],
  me: IApiEpUserAuthorized,
  product: IInvoiceFormValues["product"],
): boolean => {
  if (paymentType === "recurring" && !provider.is_support_recurring_payments) {
    return false;
  }

  const currencySupported =
    me.billing_policy_currency &&
    provider.currencies_supported[me.billing_policy_currency];

  if (!currencySupported) {
    return false;
  } else {
    const min = currencySupported.min || 0;
    const max = currencySupported.max || Number.POSITIVE_INFINITY;
    return product.price >= min && product.price < max;
  }
};

export const PaymentProviderSelector: FC<IPaymentProviderSelectorProps> = ({
  value,
  onChange,
  paymentType,
  product,
}) => {
  const {
    state: { me },
  } = useContextApp();

  const { paymentProviderList, isPaymentProviderListLoading } =
    useEpBillingPaymentProvider();

  const paymentProviderListFiltered = useMemo(() => {
    if (!paymentProviderList || product.price === undefined) {
      return;
    }

    return paymentProviderList.filter((provider) =>
      isValidProvider(provider, paymentType, me, product),
    );
  }, [me, paymentProviderList, paymentType, product]);

  useLayoutEffect(() => {
    const lastProvider = paymentProviderListFiltered?.at(-1);

    if (
      (value && isValidProvider(value, paymentType, me, product)) ||
      !lastProvider ||
      !isValidProvider(lastProvider, paymentType, me, product)
    ) {
      return;
    }

    onChange(lastProvider);
  }, [me, onChange, paymentProviderListFiltered, paymentType, product, value]);

  return (
    <>
      <UiBox mb={2}>
        <UiTypography variant="h4">
          <Trans>Payment system</Trans>
        </UiTypography>
      </UiBox>

      {isPaymentProviderListLoading ? (
        <UiBox
          display="flex"
          height={86}
          justifyContent="center"
          alignItems="center"
        >
          <UiSpinner size={24} />
        </UiBox>
      ) : (
        <>
          <UiBox>
            <UiBox display="flex" flexDirection="row" flexWrap="wrap">
              {paymentProviderListFiltered?.map((provider) => (
                <PaymentProviderCard
                  {...testAppendAttr("id", `purchase_provider_${provider.id}`)}
                  isActive={provider.id === value?.id}
                  key={provider.id}
                  provider={provider}
                  onSelect={() => onChange(provider)}
                />
              ))}
            </UiBox>
          </UiBox>
        </>
      )}
    </>
  );
};
